'use client';

import { useEffect, useMemo, useState } from 'react';
import { Bug, Camera, ImagePlus, Link2, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { BackendAuth } from '@/lib/auth-backend';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const MAX_SCREENSHOT_BYTES = 8 * 1024 * 1024;

function collectDebugContext() {
  if (typeof window === 'undefined') return {};
  const nav = navigator || {};
  const perf = performance?.getEntriesByType?.('navigation')?.[0];
  let theme = 'light';
  try {
    const html = document.documentElement;
    if (html.classList.contains('dark')) theme = 'dark';
    else if (window.matchMedia?.('(prefers-color-scheme: dark)')?.matches) theme = 'dark';
  } catch {}

  return {
    url: window.location.href,
    referrer: document.referrer || null,
    userAgent: nav.userAgent || '',
    platform: nav.platform || nav.userAgentData?.platform || '',
    language: nav.language || '',
    languages: Array.isArray(nav.languages) ? nav.languages.slice(0, 4).join(',') : '',
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    devicePixelRatio: window.devicePixelRatio || 1,
    timezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      (new Date().getTimezoneOffset() / -60).toString(),
    timestamp: new Date().toISOString(),
    online: nav.onLine,
    connection: nav.connection?.effectiveType || null,
    theme,
    pageLoadMs: perf?.duration ? Math.round(perf.duration) : null,
    sessionStorageKeys: (() => {
      try {
        return Object.keys(sessionStorage || {}).length;
      } catch {
        return null;
      }
    })(),
  };
}

async function captureScreenWithDisplayMedia() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getDisplayMedia) {
    throw new Error('getDisplayMedia not supported');
  }
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'browser' },
    audio: false,
    preferCurrentTab: true,
    selfBrowserSurface: 'include',
    surfaceSwitching: 'exclude',
  });
  try {
    const track = stream.getVideoTracks()[0];
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    await new Promise((r) => setTimeout(r, 150));

    const settings = track.getSettings();
    const width = settings.width || video.videoWidth || 1280;
    const height = settings.height || video.videoHeight || 720;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, width, height);

    return canvas.toDataURL('image/png');
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

export function BugReportButton() {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <>
      <Button
        variant="ghost"
        mode="icon"
        shape="circle"
        className="size-9 hover:bg-red-500/10 hover:[&_svg]:text-red-500"
        onClick={() => setOpen(true)}
        title={t('bugReport.button_title', { editable: false })}
        aria-label={t('bugReport.button_title', { editable: false })}
      >
        <Bug className="size-4.5!" />
      </Button>

      <BugReportDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

function BugReportDialog({ open, onOpenChange }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('normal');
  const [screenshot, setScreenshot] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [debug, setDebug] = useState({});
  const [showDebug, setShowDebug] = useState(false);

  const user = useMemo(() => (open ? BackendAuth.getUser() : null), [open]);

  useEffect(() => {
    if (open) setDebug(collectDebugContext());
  }, [open]);

  const reset = () => {
    setTitle('');
    setDescription('');
    setSeverity('normal');
    setScreenshot(null);
    setShowDebug(false);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('bugReport.not_image', { editable: false }));
      return;
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      toast.error(t('bugReport.screenshot_too_large', { editable: false }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result);
    reader.readAsDataURL(file);
  };

  const handleCaptureScreen = async () => {
    setCapturing(true);
    try {
      onOpenChange(false);
      await new Promise((r) => setTimeout(r, 250));
      const dataUrl = await captureScreenWithDisplayMedia();
      if (!dataUrl) {
        toast.error(t('bugReport.screenshot_failed', { editable: false }));
      } else {
        const approxBytes = (dataUrl.length - dataUrl.indexOf(',') - 1) * 0.75;
        if (approxBytes > MAX_SCREENSHOT_BYTES) {
          toast.error(t('bugReport.screenshot_too_large', { editable: false }));
        } else {
          setScreenshot(dataUrl);
          toast.success(t('bugReport.screenshot_captured', { editable: false }));
        }
      }
    } catch (err) {
      if (err?.name !== 'NotAllowedError') {
        toast.error(
          err?.message || t('bugReport.screenshot_failed', { editable: false }),
        );
      }
    } finally {
      setCapturing(false);
      onOpenChange(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 3 || description.trim().length < 5) {
      toast.error(t('bugReport.validation', { editable: false }));
      return;
    }
    setSubmitting(true);
    try {
      const resp = await BackendAuth.apiCallJson('/api/bug-report', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          severity,
          context: debug,
          screenshot: screenshot || null,
        }),
      });

      if (resp?.success) {
        toast.success(t('bugReport.success', { editable: false }));
        reset();
        onOpenChange(false);
      } else {
        toast.error(resp?.message || t('bugReport.error', { editable: false }));
      }
    } catch (err) {
      toast.error(err?.message || t('bugReport.error', { editable: false }));
    } finally {
      setSubmitting(false);
    }
  };

  const userLabel = user
    ? `${user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || `User #${user.id}`} (ID ${user.id})`
    : '—';
  const orgLabel = user?.organisation ?? user?.organization ?? '—';

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!submitting) onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="size-5 text-red-500" />
            {t('bugReport.title')}
          </DialogTitle>
          <DialogDescription>
            {t('bugReport.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs space-y-1.5">
            <div className="flex items-start gap-2">
              <Link2 className="size-4 mt-0.5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="text-muted-foreground">
                  {t('bugReport.url_hint')}
                </div>
                <div className="font-mono text-foreground truncate" title={debug.url}>
                  {debug.url || '—'}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pt-1 border-t border-border">
              <div>
                <span className="text-muted-foreground">{t('bugReport.ctx_user')}: </span>
                <span className="font-medium">{userLabel}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{t('bugReport.ctx_org')}: </span>
                <span className="font-medium">{orgLabel}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{t('bugReport.ctx_viewport')}: </span>
                <span className="font-mono">{debug.viewport}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{t('bugReport.ctx_theme')}: </span>
                <span>{debug.theme}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{t('bugReport.ctx_timezone')}: </span>
                <span className="font-mono">{debug.timezone}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{t('bugReport.ctx_online')}: </span>
                <span>{debug.online ? '✓' : '✗'}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowDebug((v) => !v)}
              className="text-muted-foreground hover:text-foreground underline underline-offset-2"
            >
              {showDebug ? t('bugReport.hide_debug') : t('bugReport.show_debug')}
            </button>
            {showDebug && (
              <pre className="mt-1 max-h-40 overflow-auto rounded bg-background/70 p-2 text-[10px] font-mono whitespace-pre-wrap break-all">
{JSON.stringify(debug, null, 2)}
              </pre>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bug-title">{t('bugReport.field_title')}</Label>
            <Input
              id="bug-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder={t('bugReport.field_title_placeholder', { editable: false })}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bug-desc">{t('bugReport.field_description')}</Label>
            <Textarea
              id="bug-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={8000}
              rows={6}
              placeholder={t('bugReport.field_description_placeholder', { editable: false })}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bug-severity">{t('bugReport.field_severity')}</Label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger id="bug-severity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">{t('bugReport.severity_low')}</SelectItem>
                <SelectItem value="normal">{t('bugReport.severity_normal')}</SelectItem>
                <SelectItem value="high">{t('bugReport.severity_high')}</SelectItem>
                <SelectItem value="critical">{t('bugReport.severity_critical')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>{t('bugReport.field_screenshot')}</Label>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCaptureScreen}
                disabled={capturing}
              >
                {capturing ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Camera className="size-4" />
                )}
                {t('bugReport.capture_screen')}
              </Button>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer border border-input bg-background hover:bg-accent rounded-md px-3 py-1.5">
                <ImagePlus className="size-4" />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                {t('bugReport.upload_image')}
              </label>
              {screenshot && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setScreenshot(null)}
                >
                  <X className="size-4" />
                  {t('bugReport.remove_screenshot')}
                </Button>
              )}
            </div>
            {screenshot && (
              <div className="mt-2 border border-border rounded-md overflow-hidden">
                <img
                  src={screenshot}
                  alt="screenshot preview"
                  className="max-h-48 w-auto"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="size-4 animate-spin" />}
              {t('bugReport.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
